import * as THREE from "three";

import { sampleHeight } from "../world/terrain";

export const TACTICAL_OVERLAY_RENDER_ORDER = 3;
export const TERRAIN_ANNULUS_SEGMENTS = 96;
export const TERRAIN_ANNULUS_CLEARANCE_M = 0.8;

export type TerrainAnnulusOptions = {
  color: number;
  opacity: number;
};

export type TerrainAnnulusPlacement = {
  seed: number;
  centerX: number;
  centerZ: number;
  radius: number;
  thickness: number;
  clearance?: number;
};

/**
 * Tactical boundary geometry with an explicitly closed inner/outer vertex pair.
 * Every vertex queries the same authoritative height function used by physics.
 */
export function createTerrainAnnulus(
  options: TerrainAnnulusOptions,
): THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial> {
  const vertexCount = (TERRAIN_ANNULUS_SEGMENTS + 1) * 2;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.BufferAttribute(new Float32Array(vertexCount * 3), 3),
  );
  const indices: number[] = [];
  for (let i = 0; i < TERRAIN_ANNULUS_SEGMENTS; i += 1) {
    const a = i * 2;
    const b = a + 1;
    const c = a + 2;
    const d = a + 3;
    indices.push(a, c, b, b, c, d);
  }
  geometry.setIndex(indices);
  const material = new THREE.MeshBasicMaterial({
    color: options.color,
    transparent: true,
    opacity: options.opacity,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.frustumCulled = false;
  mesh.renderOrder = TACTICAL_OVERLAY_RENDER_ORDER;
  return mesh;
}

export function updateTerrainAnnulus(
  mesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>,
  placement: TerrainAnnulusPlacement,
): void {
  const clearance = placement.clearance ?? TERRAIN_ANNULUS_CLEARANCE_M;
  const key = `${placement.seed}|${placement.centerX.toFixed(2)}|${placement.centerZ.toFixed(2)}|${placement.radius.toFixed(2)}|${placement.thickness.toFixed(2)}|${clearance.toFixed(2)}`;
  if (mesh.userData.terrainAnnulusKey === key) return;
  mesh.userData.terrainAnnulusKey = key;

  const positions = mesh.geometry.getAttribute("position") as THREE.BufferAttribute;
  const half = placement.thickness * 0.5;
  const inner = Math.max(0.05, placement.radius - half);
  const outer = placement.radius + half;
  for (let i = 0; i <= TERRAIN_ANNULUS_SEGMENTS; i += 1) {
    const angle = (i / TERRAIN_ANNULUS_SEGMENTS) * Math.PI * 2;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const ix = placement.centerX + cos * inner;
    const iz = placement.centerZ + sin * inner;
    const ox = placement.centerX + cos * outer;
    const oz = placement.centerZ + sin * outer;
    positions.setXYZ(i * 2, ix, sampleHeight(placement.seed, ix, iz) + clearance, iz);
    positions.setXYZ(i * 2 + 1, ox, sampleHeight(placement.seed, ox, oz) + clearance, oz);
  }
  positions.needsUpdate = true;
  mesh.geometry.computeBoundingSphere();
}
